/**
 * El mapa de vehículos, independiente del proveedor.
 *
 * Monta UN adaptador según lo que diga el servidor (hoy Google), le pasa datos
 * neutrales y lo libera al desmontarse. No crea un segundo mapa oculto para
 * tener el respaldo listo (especificación de mapas, § 5.2).
 *
 * Si no hay mapa —sin clave, apagado o con falla— lo dice en palabras simples y
 * no tapa nada: la pantalla que lo usa sigue mostrando todos los datos.
 */

import { useEffect, useRef, useState } from "react";
import type { RastreoMapConfig } from "@core/contracts/rastreo.ts";
import type { MapAdapter, MapStatus, MapTrackPoint, MapVehicle } from "@web/lib/maps/map-adapter.ts";
import { GoogleMapsAdapter } from "@web/lib/maps/google-maps-adapter.ts";

export function VehicleMap({
  config,
  vehicles,
  track,
  selectedId,
  onSelect,
  fill = false,
  fitKey = "",
  follow = null,
  unavailableHint = "Los datos siguen disponibles abajo.",
  createAdapter = (apiKey, select) => new GoogleMapsAdapter(apiKey, select),
}: {
  config: RastreoMapConfig | undefined;
  vehicles: MapVehicle[];
  track: MapTrackPoint[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * Ocupa todo el alto de su contenedor, sin borde ni radio: es el mapa de la
   * consola de Rastreo, que va de borde a borde como en Traccar.
   */
  fill?: boolean;
  /**
   * Cuándo volver a encuadrar, además de al cambiar de vehículo: por ejemplo, al
   * cambiar el período. Los puntos que llegan en vivo NO reencuadran el mapa: si
   * lo hicieran, el zoom saltaría cada 5 s.
   */
  fitKey?: string;
  /** «Seguir al equipo»: cada vez que cambia, el mapa se centra ahí sin tocar el zoom. */
  follow?: MapTrackPoint | null;
  /** Qué decir cuando no hay mapa: dónde siguen los datos en la pantalla que lo usa. */
  unavailableHint?: string;
  /** Para pruebas: permite inyectar un adaptador falso. */
  createAdapter?: (apiKey: string, onSelect: (id: string) => void) => MapAdapter;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const adapter = useRef<MapAdapter | null>(null);
  const select = useRef(onSelect);
  const factory = useRef(createAdapter);
  const [status, setStatus] = useState<MapStatus>({ state: "loading" });
  // Encuadre: una vez al tener datos y otra al cambiar de vehículo o de recorrido.
  const framedFor = useRef<string | null>(null);

  useEffect(() => {
    select.current = onSelect;
  }, [onSelect]);

  const apiKey = config?.provider === "google" ? config.googleMapsKey : null;

  useEffect(() => {
    if (!apiKey || !container.current) return;
    // El adaptador se crea una sola vez por clave: los datos llegan por los efectos de abajo.
    const instance = factory.current(apiKey, (id) => select.current(id));
    adapter.current = instance;
    const unsubscribe = instance.subscribeStatus(setStatus);
    void instance.initialize(container.current);
    return () => {
      unsubscribe();
      instance.dispose();
      adapter.current = null;
      framedFor.current = null;
    };
  }, [apiKey]);

  useEffect(() => {
    adapter.current?.renderVehicles(vehicles);
  }, [vehicles, status.state]);

  useEffect(() => {
    adapter.current?.renderRecordedTrack(track);
  }, [track, status.state]);

  useEffect(() => {
    adapter.current?.setSelectedVehicle(selectedId);
  }, [selectedId, status.state]);

  // Se encuadra al elegir otro vehículo, al llegar su recorrido y al cambiar `fitKey`;
  // no por cada punto nuevo (RR-18).
  const hasTrack = track.length > 0;
  useEffect(() => {
    if (status.state !== "ready" || vehicles.length === 0) return;
    const key = `${selectedId ?? "flota"}:${hasTrack ? "con-recorrido" : "sin-recorrido"}:${fitKey}`;
    if (framedFor.current === key) return;
    framedFor.current = key;
    adapter.current?.fitToContent();
  }, [status.state, vehicles.length, selectedId, hasTrack, fitKey]);

  const followLat = follow?.latitude ?? null;
  const followLng = follow?.longitude ?? null;
  useEffect(() => {
    if (status.state !== "ready" || followLat === null || followLng === null) return;
    adapter.current?.panTo({ latitude: followLat, longitude: followLng });
  }, [status.state, followLat, followLng]);

  if (!config) {
    return <MapMessage fill={fill} title="Cargando la configuración del mapa…" />;
  }
  if (config.provider === "none") {
    return <MapMessage fill={fill} title="Mapa no disponible" body={`${config.reason} ${unavailableHint}`} />;
  }

  return (
    <div className={`relative ${fill ? "h-full w-full" : ""}`}>
      <div
        ref={container}
        role="region"
        aria-label="Mapa de vehículos"
        className={
          fill
            ? "h-full min-h-[320px] w-full bg-gd-mist"
            : "h-[420px] w-full overflow-hidden rounded-2xl border border-gd-line bg-gd-mist sm:h-[520px]"
        }
      />
      {status.state !== "ready" ? (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-white/80 p-6 text-center ${
            fill ? "" : "rounded-2xl"
          }`}
        >
          <p className="max-w-sm text-[15px] text-gd-navy" role="status">
            {status.state === "loading" ? "Cargando el mapa…" : status.reason}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function MapMessage({ title, body, fill = false }: { title: string; body?: string; fill?: boolean }) {
  return (
    <div
      className={
        fill
          ? // Arriba y a la derecha: la ficha del equipo se abre abajo a la izquierda y no lo tapa.
            "gd-map-empty flex h-full min-h-[320px] w-full flex-col items-center p-4 lg:items-end lg:p-6"
          : "flex h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-gd-line-strong bg-gd-mist p-6 text-center"
      }
    >
      <div className={fill ? "max-w-sm rounded-2xl border border-gd-line bg-white/95 px-4 py-3 text-center shadow-[var(--shadow-card)]" : ""}>
        <p className="font-display text-[15px] font-bold text-gd-navy" role="status">
          {title}
        </p>
        {body ? <p className="mt-1 max-w-md text-sm text-gd-muted">{body}</p> : null}
      </div>
    </div>
  );
}
